/**
 * Workflow Validation Service
 * 
 * Client-side validation for workflows before execution/save
 */

export interface ValidationError {
  type: 'error' | 'warning';
  message: string;
  nodeId?: string;
  field?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

export const validationApi = {
  /**
   * Validate a workflow (client-side validation)
   */
  validateWorkflow: (workflow: any): ValidationResult => {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];

    const nodes = workflow.definition?.nodes || [];

    // Validate Respond to Trigger nodes
    const respondToTriggerNodes = nodes.filter(
      (node: any) => node.type === 'respond-to-trigger' || node.type === 'respond-to-chat'
    );

    if (respondToTriggerNodes.length > 1) {
      errors.push({
        type: 'error',
        message: `Only one "Respond to Trigger" node is allowed per workflow. Found ${respondToTriggerNodes.length} nodes.`,
        nodeId: respondToTriggerNodes[1].id,
      });
    }

    // Validate webhook trigger response modes
    const webhookTriggers = nodes.filter((node: any) => node.type === 'webhook-trigger');

    for (const webhook of webhookTriggers) {
      const responseMode = webhook.data?.responseMode;

      if (responseMode === 'respond-node') {
        if (respondToTriggerNodes.length === 0) {
          errors.push({
            type: 'error',
            message: 'Webhook trigger is set to "Use Respond to Trigger node" but no Respond to Trigger node exists in the workflow.',
            nodeId: webhook.id,
            field: 'responseMode',
          });
        }
      }
    }

    // Validate chat triggers
    const chatTriggers = nodes.filter((node: any) => node.type === 'chat-trigger');

    for (const chat of chatTriggers) {
      const responseMode = chat.data?.responseMode;

      if (responseMode === 'respondToTrigger' || responseMode === 'respondToChat') {
        if (respondToTriggerNodes.length === 0) {
          errors.push({
            type: 'error',
            message: 'Chat trigger is set to "Use Respond to Trigger node" but no Respond to Trigger node exists in the workflow.',
            nodeId: chat.id,
            field: 'responseMode',
          });
        }
      }
    }

    // Validate MCP triggers
    const mcpTriggers = nodes.filter((node: any) => node.type === 'mcp-trigger');

    for (const mcp of mcpTriggers) {
      const responseMode = mcp.data?.responseMode;

      if (responseMode === 'respond-node') {
        if (respondToTriggerNodes.length === 0) {
          errors.push({
            type: 'error',
            message: 'MCP trigger is set to "Use Respond to Trigger node" but no Respond to Trigger node exists in the workflow.',
            nodeId: mcp.id,
            field: 'responseMode',
          });
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  },
};
